import { useEffect, useMemo, useState } from "react"
import { createColumnHelper } from "@tanstack/react-table"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentPlusIcon,
  EyeIcon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  NoSymbolIcon,
  BookOpenIcon,
} from "@heroicons/react/24/solid"
import to from "await-to-js"
import { LoaderFunction, useNavigate, useLoaderData } from "react-router-dom"
import ReactSimpleImageViewer from "react-simple-image-viewer"
import { toast } from "react-toastify"
import { Tooltip } from "flowbite-react"

import ReactTable from "components/ui/react-table/ReactTable"
import DarkPagination from "components/ui/pagination/DarkPagination"
import { Course, CMSCourseListResponse } from "utils/types"
import API from "utils/axiosInstance"
import DefBreadcrumb from "components/ui/breadcrumb/DefBreadcrumb"
import ButtonWithIcon from "components/ui/button/ButtonWithIcon"
import EmployeeDashboardWrapper from "components/layouts/EmployeeDashboardWrapper"
import { getCMSSegments } from "utils/helper"
import api from "utils/axiosInstance"
import usePagination from "hooks/usePagination"
import CMSDefaultTableListFilter from "components/cms/CMSDefaultTableListFilter"
import useFlashMessage from "hooks/useFlashMessage"
import FileInput from "components/ui/form/FileInput"
import {
  useMutationPublishCourse,
  useMutationUploadThumbnailCourse,
  useMutationDeactivateCourse,
} from "utils/api/cms/mutations/hooks"
import Modal from "components/ui/modal/Modal"
import Typography from "components/ui/typography"
import Button from "components/ui/button/Button"

const CourseManagement: React.FC = () => {
  const response = useLoaderData() as CMSCourseListResponse<Course>
  const segments = getCMSSegments()
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState("")
  const [perPage, setPerpage] = useState(10)
  const [coursesResponse, setCoursesResponse] =
    useState<CMSCourseListResponse<Course>>()
  const [previewThumbnailID, setPreviewThumbnailID] = useState<number>()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [modalPublish, setModalPublish] = useState<{
    isOpen: boolean
    courseID: string
    courseName: string
  }>()
  const [modalDeactivate, setModalDeactivate] = useState<{
    isOpen: boolean
    courseID: string
    courseName: string
  }>()

  useFlashMessage(
    "success",
    "courseUpsertSuccess",
    "Berhasil menyimpan modul belajar"
  )

  const { mutate: mutateUploadFile } = useMutationUploadThumbnailCourse({
    onSuccess(data, variables) {
      if (!data.success) return

      toast.success("Berhasil upload thumbnail")
      setCoursesResponse((prev) => {
        if (!prev) return
        return {
          ...prev,
          data: prev.data.map((course) => {
            if (course.id.toString() === variables.courseID) {
              return {
                ...course,
                thumbnail: data.data.thumbnail,
              }
            }
            return course
          }),
        }
      })
    },
  })

  const { mutate: mutatePublishCourse } = useMutationPublishCourse({
    onSuccess(data, variables) {
      toast.success("Berhasil publish kelas")
      handleModalPublish()
      setCoursesResponse((prev) => {
        if (!prev) return
        return {
          ...prev,
          data: prev.data.map((course) => {
            if (course.id.toString() === variables.courseID) {
              return {
                ...course,
                ...data.data,
              }
            }
            return course
          }),
        }
      })
    },
  })

  const { mutate: mutateDeactivateCourse } = useMutationDeactivateCourse({
    onSuccess(data, variables) {
      toast.success("Berhasil deactivate kelas")
      handleModalDeactivate()
      setCoursesResponse((prev) => {
        if (!prev) return
        return {
          ...prev,
          data: prev.data.map((course) => {
            if (course.id.toString() === variables.courseID) {
              return {
                ...course,
                ...data.data,
              }
            }
            return course
          }),
        }
      })
    },
  })

  useEffect(() => {
    if (response) {
      setCoursesResponse(response)
      setTotalPages(response.meta!.last_page)

      if (response.meta!.current_page > 1) {
        setInitialPage(response.meta!.current_page - 1)
      }

      setShouldFetch(true)
    }
  }, [response])

  const handleModalPublish = (courseID?: string, courseName?: string) => {
    setModalPublish((prev) => ({
      isOpen: !prev?.isOpen,
      courseID: courseID ?? "",
      courseName: courseName ?? "",
    }))
  }

  const handleModalDeactivate = (courseID?: string, courseName?: string) => {
    setModalDeactivate((prev) => ({
      isOpen: !prev?.isOpen,
      courseID: courseID ?? "",
      courseName: courseName ?? "",
    }))
  }

  const coursesFetcher = async () => {
    const page = Math.min(currentPage + 1, totalPages)
    const [_, resultResponse] = await to<CMSCourseListResponse<Course>>(
      api.cms.getCourses({
        page: page,
        keyword: keyword,
        limit: perPage,
      })
    )

    setTotalPages(resultResponse!.meta!.last_page)
    setCoursesResponse(resultResponse)
  }

  const courseNameSearchHandler = (keywordVal: string) => {
    setForced(true)
    setCurrentPage(0)
    setKeyword(keywordVal)
  }
  const perPageHandler = (selected: number) => {
    setForced(true)
    setCurrentPage(0)
    setPerpage(selected)
  }

  const handleChangeFile =
    (courseID: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      mutateUploadFile({
        courseID: courseID,
        payload: { thumbnail: file },
      })
    }

  const handleDeleteFile = (courseID: number) => {
    setCoursesResponse((prev) => {
      if (!prev) return
      return {
        ...prev,
        data: prev.data.map((course) => {
          if (course.id === courseID) {
            return {
              ...course,
              thumbnail: "",
            }
          }

          return course
        }),
      }
    })
  }

  const handlePreview = (index?: number) => {
    setPreviewThumbnailID(index)
    setIsPreviewOpen((prev) => !prev)
  }

  const {
    totalPages,
    currentPage,
    initialPage,
    setTotalPages,
    setCurrentPage,
    setInitialPage,
    setShouldFetch,
    setForced,
  } = usePagination(coursesFetcher, [keyword, perPage])

  const colHelper = createColumnHelper<Course>()
  const courseColumns = [
    colHelper.accessor("title", {
      header: () => "Judul Modul Belajar",

    }),
    colHelper.accessor("partnerships", {
      header: () => "Partnership",
      cell: (props) => (
        <p>
          {`${props.getValue()?.full_name}`}
        </p>
      ),
    }),
    colHelper.accessor("thumbnail", {
      header: () => "Thumbnail",
      cell: (props) => (
        <div className="relative w-[120px] h-[85px]">
          <FileInput
            label="Upload Gambar"
            name="thumbnail"
            value={props.getValue()}
            onChange={handleChangeFile(props.row.original.id.toString())}
            onDelete={() => handleDeleteFile(props.row.original.id)}
            onPreview={() => handlePreview(props.row.index)}
            accept="image/png, image/jpg"
          />
        </div>
      ),
    }),
    colHelper.accessor("lecturer", {
      header: () => "Mentor",
      cell: (props) => props.getValue() ?? "-",
    }),
    colHelper.accessor("price", {
      header: () => "Harga",
      cell: (props) => (
        <dl className="max-w-md text-white divide-y divide-gray-200">
          <div className="flex flex-col pb-3">
            <dt className="mb-1 text-gray-400 md:text-lg">Harga Asli</dt>
            <dd className="text-lg font-semibold">{`${props.getValue().currency_text
              } ${props.getValue().master_price_text}`}</dd>
          </div>
          <div className="flex flex-col pb-3">
            <dt className="mb-1 text-gray-400 md:text-lg">Harga Terkini</dt>
            <dd className="text-lg font-semibold">{`${props.getValue().currency_text
              } ${props.getValue().current_price_text}`}</dd>
          </div>
        </dl>
      ),
    }),
    colHelper.accessor("is_active", {
      header: () => "Status Modul",
      cell: (props) => {
        if (!props.getValue()) {
          return (
            <span className="text-xs font-medium mr-2 px-2.5 py-0.5 rounded bg-yellow-900 text-yellow-300">
              PENDING
            </span>
          )
        }

        return (
          <span className="text-xs font-medium mr-2 px-2.5 py-0.5 rounded bg-green-900 text-green-300">
            Aktif
          </span>
        )
      },
    }),
    colHelper.accessor("categories", {
      header: () => "Kategori",
      cell: (props) => {
        return (
          <ul className="max-w-md space-y-1 text-white list-none list-inside">
            {props
              .getValue()
              ?.map((category) => <li key={category.id}>{category.name}</li>)}
          </ul>
        )
      },
    }),
    colHelper.display({
      id: "actions",
      header: () => "AKSI",
      cell: (props) => (
        <div className="flex flex-row w-fit space-x-3">
          <Tooltip content="Edit">
            <ButtonWithIcon
              class="mx-auto border-2 border-transparent bg-yellow-400 text-white hover:bg-yellow-300 hover:border-yellow-300"
              type="button"
              action={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                return navigate(`edit/${props.row.original.id}`)
              }}
              sizeClass="p-1"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </ButtonWithIcon>
          </Tooltip>
          <Tooltip content="Detail">
            <ButtonWithIcon
              class="mx-auto border-2 border-transparent bg-blue-600 text-white hover:bg-blue-500 hover:border-blue-500"
              type="button"
              action={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                return navigate(`detail/${props.row.original.id}`)
              }}
              sizeClass="p-1"
            >
              <EyeIcon className="h-5 w-5" />
            </ButtonWithIcon>
          </Tooltip>
          {!props.row.original.is_active && (
            <Tooltip content="Publish">
              <ButtonWithIcon
                disabled={!props.row.original.publishable}
                class="mx-auto border-2 border-transparent bg-green-600 text-white hover:bg-green-500 hover:border-green-500"
                type="button"
                action={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  handleModalPublish(
                    props.row.original.id.toString(),
                    props.row.original.title
                  )
                }}
                sizeClass="p-1"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </ButtonWithIcon>
            </Tooltip>
          )}
          {props.row.original.is_active && (
            <Tooltip content="Deactivate">
              <ButtonWithIcon
                class="mx-auto border-2 border-transparent bg-red-600 text-white hover:bg-red-500 hover:border-red-500"
                type="button"
                action={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault()
                  handleModalDeactivate(
                    props.row.original.id.toString(),
                    props.row.original.title
                  )
                }}
                sizeClass="p-1"
              >
                <NoSymbolIcon className="h-5 w-5" />
              </ButtonWithIcon>
            </Tooltip>
          )}
          <Tooltip content="Kategori Pertanyaan">
            <ButtonWithIcon
              class="mx-auto border-2 border-transparent bg-purple-600 text-white hover:bg-purple-500 hover:border-purple-500"
              type="button"
              action={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                return navigate(`category-question/${props.row.original.id}`)
              }}
              sizeClass="p-1"
            >
              <BookOpenIcon className="h-5 w-5" />
            </ButtonWithIcon>
          </Tooltip>
        </div>
      ),
    }),
  ]

  const previewImages = useMemo(
    () => coursesResponse?.data.map((course) => course.thumbnail),
    [coursesResponse]
  )

  return (
    <EmployeeDashboardWrapper>
      <DefBreadcrumb
        segments={segments}
        prefix={"cms"}
      />
      <h2 className="text-xl mt-4 text-primary font-semibold leading-loose tracking-wide">
        Pengaturan Konten Belajar
      </h2>
      <div className="flex flex-wrap justify-between gap-4 my-6">
        <ButtonWithIcon
          action={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            return navigate("create")
          }}
          type="submit"
          class="border-2 border-transparent bg-primary text-white space-x-2 hover:bg-transparent hover:text-white hover:border-primary"
        >
          <DocumentPlusIcon className="h-6 w-6 text-white" />
          <span>Tambah Modul Pembelajaran</span>
        </ButtonWithIcon>
        <CMSDefaultTableListFilter
          pageSelector
          searchPlaceHolder="Cari nama konten belajar"
          wrapperClass="flex flex-row space-x-6"
          onSearchHandler={courseNameSearchHandler}
          perPageHandler={perPageHandler}
        />
      </div>

      {coursesResponse && (
        <>
          <ReactTable
            columns={courseColumns}
            data={coursesResponse!.data}
          />
          <div className="flex pb-8 items-center ml-auto">
            <DarkPagination
              nexLabel={<ChevronRightIcon className="h-5 w-5 text-gray-400" />}
              previousLabel={
                <ChevronLeftIcon className="h-5 w-5 text-gray-400" />
              }
              pageCount={totalPages}
              pageRangeDisplayed={2}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              initialPage={initialPage}
            />
          </div>
          {isPreviewOpen && (
            <ReactSimpleImageViewer
              src={previewImages ?? []}
              currentIndex={previewThumbnailID}
              onClose={handlePreview}
              disableScroll
              backgroundStyle={{
                backgroundColor: "rgba(0,0,0,0.9)",
              }}
              closeOnClickOutside={true}
            />
          )}
        </>
      )}

      <Modal
        header="Publish Kelas"
        onclose={handleModalPublish}
        show={!!modalPublish?.isOpen}
        body={
          <Typography className="text-white">
            Apakah Anda yakin untuk publish kelas{" "}
            <b>{modalPublish?.courseName}</b>?
          </Typography>
        }
        footer={
          <div className="space-x-2 ml-auto">
            <Button
              onClick={() => handleModalPublish()}
              variant="outlined"
              className="text-gray-300 border-gray-300"
            >
              Batal
            </Button>
            <Button
              onClick={() =>
                mutatePublishCourse({
                  courseID: modalPublish?.courseID ?? "",
                })
              }
            >
              Publish
            </Button>
          </div>
        }
      />

      <Modal
        header="Deactivate Kelas"
        onclose={handleModalDeactivate}
        show={!!modalDeactivate?.isOpen}
        body={
          <Typography className="text-white">
            Apakah Anda yakin untuk deactivate kelas{" "}
            <b>{modalDeactivate?.courseName}</b>?
          </Typography>
        }
        footer={
          <div className="space-x-2 ml-auto">
            <Button
              onClick={() => handleModalDeactivate()}
              variant="outlined"
              className="text-gray-300 border-gray-300"
            >
              Batal
            </Button>
            <Button
              onClick={() =>
                mutateDeactivateCourse({
                  courseID: modalDeactivate?.courseID ?? "",
                })
              }
            >
              Deactivate
            </Button>
          </div>
        }
      />
    </EmployeeDashboardWrapper>
  )
}

export default CourseManagement

export const loadCourses: LoaderFunction = async (): Promise<
  CMSCourseListResponse<Course>
> => {
  const searchParams = new URLSearchParams(window.location.search)
  const response = await API.cms.getCourses({
    keyword: searchParams.has("search")
      ? (searchParams.get("search") as string)
      : "",
    page: searchParams.has("page") ? +(searchParams.get("page") as string) : 1,
    limit: 10,
  })

  return response
}
